<template>
  <template v-if="showButton">
    <div v-if="showRemainingTasks" class="d-flex justify-end mr-4">
      <div class="badge">{{ remainingTasks }} tasks</div>
    </div>

    <v-list-item class="ma-0" @click="goToPremiumWizard">
      <v-btn-primary :class="buttonClass" prepend-icon="mdi-rotate-right">
        {{ buttonText }}
      </v-btn-primary>
    </v-list-item>
  </template>
</template>
<script setup>
import { onMounted, computed, ref, onUnmounted } from 'vue';
import { getWizard } from './api/getWizard';
import { useRootStore } from '../../stores/root';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { eventBus } from '../../helpers/eventBus';
import wizardJSON from './helpers/wizard.json';
import { getOrgAppAccess } from './api/getOrgAppAccess';

const rootStore = useRootStore();
const { orgId, isOrgAdmin, isAdmin, enterpriseClient } = storeToRefs(rootStore);
const totalPages = ref([]);
const totalNextSteps = ref([]);
const completedPages = ref([]);
const completedNextSteps = ref([]);
const wizardComplete = ref(false);
const mountedFinished = ref(false);
const route = useRoute();
const orgApps = ref([]);

const hasPermission = computed(() => {
  return isOrgAdmin.value || isAdmin.value;
});

const rewardsOn = computed(() => orgApps.value.includes('st_rewards'));
const surveysOn = computed(() => orgApps.value.includes('st_surveys'));
const rewardSteps = computed(() =>
  totalNextSteps.value
    .filter((item) => item.page === 'rewards')
    .map((item) => item.id),
);
const surveySteps = computed(() =>
  totalNextSteps.value
    .filter((item) => item.page === 'surveys')
    .map((item) => item.id),
);

const filteredCompletedPages = computed(() => {
  return completedPages.value.filter((item) => {
    if (item === 'rewards' && !rewardsOn.value) {
      return false;
    }
    if (item === 'surveys' && !surveysOn.value) {
      return false;
    }
    return true;
  });
});

const filteredCompletedNextSteps = computed(() => {
  return completedNextSteps.value.filter((item) => {
    if (!rewardsOn.value) {
      if (rewardSteps.value.includes(item)) {
        return false;
      }
    }
    if (!surveysOn.value) {
      if (surveySteps.value.includes(item)) {
        return false;
      }
    }
    return true;
  });
});

const filteredTotalNextSteps = computed(() => {
  return totalNextSteps.value.filter((item) => {
    if (item.page === 'rewards' && !rewardsOn.value) {
      return false;
    }
    if (item.page === 'surveys' && !surveysOn.value) {
      return false;
    }
    return true;
  });
});

const filteredTotalPages = computed(() => {
  return totalPages.value.filter((item) => {
    if (item === 'rewards' && !rewardsOn.value) {
      return false;
    }
    if (item === 'surveys' && !surveysOn.value) {
      return false;
    }
    return true;
  });
});

const remainingTasks = computed(() => {
  if (
    !filteredCompletedPages.value ||
    filteredCompletedPages.value.length === 0
  ) {
    return 0;
  }

  const totalRemainingPages =
    filteredTotalPages.value?.length - filteredCompletedPages.value.length;
  const totalRemainingNextSteps =
    filteredTotalNextSteps.value?.length -
    filteredCompletedNextSteps.value.length;
  return totalRemainingPages + totalRemainingNextSteps;
});

const showRemainingTasks = computed(() => {
  return remainingTasks.value > 0 && filteredCompletedPages.value.length > 0;
});

const premiumEnabled = computed(() => {
  return orgApps.value?.includes('crew_portal');
});

const buttonText = computed(() => {
  if (filteredCompletedPages.value.length === 0) {
    return 'Begin Premium Setup';
  }
  return 'Finish Premium Setup';
});

const buttonClass = computed(() => {
  if (route.path.includes('premium-wizard')) {
    return 'light-color';
  }
  return '';
});

function goToPremiumWizard() {
  window.location.href = '/portal/premium-wizard';
}

const showButton = computed(() => {
  const mcdOnly = enterpriseClient.value === 'McDonalds';
  return (
    premiumEnabled.value &&
    !wizardComplete.value &&
    mountedFinished.value &&
    hasPermission.value &&
    mcdOnly
  );
});

async function refresh() {
  const wizardResponse = await getWizard(orgId.value);
  if (wizardResponse) {
    completedPages.value = wizardResponse.completedPages;
    completedNextSteps.value = wizardResponse.completedNextSteps;
    wizardComplete.value = wizardResponse.wizardComplete;
  }
}

async function fetchOrgApps() {
  const result = await getOrgAppAccess(orgId.value);
  orgApps.value = result;
}

onMounted(async () => {
  await rootStore.waitForUserToBeLoaded();
  await fetchOrgApps();

  if (!premiumEnabled.value && !hasPermission.value) {
    return;
  }
  const wizardResponse = await getWizard(orgId.value);

  if (wizardResponse) {
    completedPages.value = wizardResponse.completedPages;
    completedNextSteps.value = wizardResponse.completedNextSteps;
    totalPages.value = wizardJSON.pages;
    totalNextSteps.value = wizardJSON.steps;
    wizardComplete.value = wizardResponse.wizardComplete;
  }
  eventBus.on('step-updated', refresh);
  eventBus.on('orgAppsUpdated', fetchOrgApps);
  mountedFinished.value = true;
});

onUnmounted(() => {
  eventBus.off('step-updated', refresh);
  eventBus.off('orgAppsUpdated', fetchOrgApps);
});
</script>
<style scoped>
.badge {
  background-color: rgb(var(--v-theme-surface-warning));
  color: rgb(var(--v-theme-content-onReverse-high));
  display: flex;
  padding: 4px 6px;
  justify-content: center;
  align-items: center;
  border-radius: 20px;
  font-size: 12px;
  margin-bottom: -15px;
  z-index: 5;
}

.light-color {
  background-color: rgb(var(--v-theme-surface-primary-low));
  color: rgb(var(--v-theme-content-primary));
}
</style>
